/**
 * 轻图测试创建面板
 *
 * 职责：
 * 1. 展示轻图测试两步绘制流程的 UI（缓冲区/停车区/危险区）
 * 2. 用户填写备注后提交
 *
 * 层级：components（纯展示）
 */
import React from 'react';
import { Button, Form, Input } from 'antd';
import { LightMapAssistType } from '@/utils/constant';
import type { LightMapDraft, LightMapPairFormValues, LightMapWarningFormValues } from '../utils/lightMap';

type Props = {
  draft: LightMapDraft;
  onContinueParking: () => void;
  onRepaint: (target: 'buffer' | 'parking' | 'warning') => void;
  onCancel: () => void;
  onSubmit: (values: LightMapPairFormValues | LightMapWarningFormValues) => void;
};

const LightMapCreationPanel = ({
  draft,
  onContinueParking,
  onRepaint,
  onCancel,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm();
  const isWarning = draft.subType === LightMapAssistType.WARNING_ZONE;
  const isParkingFormVisible = !isWarning && (draft.stage === 'parkingReady' || draft.stage === 'submitting');
  const isWarningFormVisible = isWarning && (draft.stage === 'warningReady' || draft.stage === 'submitting');
  const isSubmitting = draft.stage === 'submitting';

  const handleSubmit = () => {
    form.validateFields().then((values) => onSubmit(values)).catch(() => undefined);
  };

  return (
    <section className="light-map-creation-panel" aria-label="轻图测试创建流程">
      <button
        type="button"
        className="light-map-close-btn"
        onClick={onCancel}
        aria-label="取消轻图测试创建"
        disabled={isSubmitting}
      >
        ×
      </button>
      <div className="light-map-creation-title">轻图测试</div>

      {!isWarning && (
        <div className="light-map-creation-step">
          <span className={draft.buffer ? 'completed' : 'active'}>1. 轻图缓冲区</span>
          <span className="separator">→</span>
          <span className={draft.parking || draft.stage === 'parkingDrawing' ? 'active' : ''}>
            2. 轻图停车区
          </span>
        </div>
      )}

      {draft.stage === 'bufferDrawing' && (
        <p className="light-map-creation-tip">请在地图上绘制轻图缓冲区。</p>
      )}

      {draft.stage === 'bufferReady' && (
        <>
          <p className="light-map-creation-tip">缓冲区绘制完成，请继续绘制停车区。</p>
          <div className="form-btn-container">
            <Button className="repaint-btn" onClick={() => onRepaint('buffer')}>
              重绘缓冲区
            </Button>
            <Button type="primary" className="create-btn" onClick={onContinueParking}>
              完成缓冲区，绘制停车区
            </Button>
          </div>
        </>
      )}

      {draft.stage === 'parkingDrawing' && (
        <p className="light-map-creation-tip">请在地图上绘制轻图停车区。</p>
      )}

      {draft.stage === 'warningDrawing' && (
        <p className="light-map-creation-tip">请在地图上绘制轻图危险区。</p>
      )}

      {(isParkingFormVisible || isWarningFormVisible) && (
        <Form form={form} layout="horizontal" onFinish={onSubmit}>
          <div className="light-map-readonly-fields" aria-label="固定生效配置">
            <div className="light-map-readonly-row">
              <span className="label">车辆生效范围：</span>
              <span className="value">全部生效</span>
            </div>
            <div className="light-map-readonly-row">
              <span className="label">生效类别：</span>
              <span className="value">长期生效</span>
            </div>
          </div>
          {isWarning ? (
            <Form.Item
              label="危险区备注（必填）"
              name="remark"
              rules={[{ required: true, message: '请填写危险区备注' }]}
            >
              <Input.TextArea rows={2} maxLength={80} showCount />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label="缓冲区备注（必填）"
                name="bufferRemark"
                rules={[{ required: true, message: '请填写轻图缓冲区备注' }]}
              >
                <Input.TextArea rows={2} maxLength={80} showCount />
              </Form.Item>
              <Form.Item
                label="停车区备注（必填）"
                name="parkingRemark"
                rules={[{ required: true, message: '请填写轻图停车区备注' }]}
              >
                <Input.TextArea rows={2} maxLength={80} showCount />
              </Form.Item>
            </>
          )}
          <div className="form-btn-container">
            {isWarning ? (
              <Button className="repaint-btn" disabled={isSubmitting} onClick={() => onRepaint('warning')}>
                重绘危险区
              </Button>
            ) : (
              <>
                <Button className="repaint-btn" disabled={isSubmitting} onClick={() => onRepaint('buffer')}>
                  重绘缓冲区
                </Button>
                <Button className="repaint-btn" disabled={isSubmitting} onClick={() => onRepaint('parking')}>
                  重绘停车区
                </Button>
              </>
            )}
            <Button
              type="primary"
              htmlType="button"
              className="create-btn"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              创建
            </Button>
          </div>
        </Form>
      )}
    </section>
  );
};

export default React.memo(LightMapCreationPanel);
